import { HttpClient, HttpContext } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { ENV_CONFIG } from '@digilize/shared/utils/tokens';
import { AppContext } from '@plys/src/application/app.context';
import { SKIP_ERROR_INTERCEPTOR } from '@plys/src/application/errors/error.interceptor';
import { AuthStrategy, UpdateUserPayload, User } from '@plys/src/auth/application/auth.strategy';
import { TokenRepository } from '@plys/src/auth/infrastructure/token.repository';
import { Auth, CinevillePassModel, EnvConfig } from '@shared/definitions';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, concatMap, map, Observable, of, tap, zip } from 'rxjs';

export interface UserResponse {
  id: string;
  email: string;
  phone: string | null;
  password: string | null;
  password_confirm: string | null;
  first_name: string;
  last_name: string;
  display_name: string;
  role: number;
  activated: string;
  privacy: number;
  privacy_hide_as_visitor: boolean | null;
  post_code: string | null;
  city: string | null;
  country: string | null;
  street: string | null;
  house_number: string | null;
  street_number: string | null;
  agreement1: boolean;
  agreement2: boolean;
  agreement3: boolean;
  nickname: string;
  gender: string;
  birthday: string | null;
  age: number | null;
  description: string | null;
  password_created: boolean;
  /** The shops this account can reach. Absent on an older backend, which is read as "unknown". */
  shop_ids?: string[];
  company_ids: string[];
  liked: boolean | null;
  photo_id: string | null;
  photo: string | null;
  newsletter: boolean | null;
}

export interface ChangeProfileModel {
  email: string | null;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  nickname: string | null;
  description: string | null;
  gender: string | null;
  birthday: string | null;
}

export interface AuthResponse {
  token: string;
  refresh_token: string;
}

@Injectable({
  providedIn: 'root',
})
export class JwtAuthStrategy extends AuthStrategy {
  private readonly baseUrl = `${this.config.api}`;

  constructor(
    @Inject(ENV_CONFIG) private readonly config: EnvConfig,
    private readonly client: HttpClient,
    private readonly injector: Injector,
    private readonly tokenRepository: TokenRepository,
    private readonly appContext: AppContext
  ) {
    super();
  }

  logout(): Observable<boolean> {
    this.tokenRepository.clear();
    return of(true);
  }

  register(data: {
    email: string;
    firstname: string;
    lastname: string;
    password: string;
    confirmPassword: string;
    phone: string;
  }): Observable<User> {
    return this.executeCaptcha('register').pipe(
      concatMap((recaptchaToken: string) => {
        const { email, firstname, lastname, password, confirmPassword, phone } = data;
        return this.client.post<Auth>(
          `${this.baseUrl}/v1.0/auth/create`,
          {
            email,
            first_name: firstname,
            last_name: lastname,
            password: password,
            password_confirm: confirmPassword,
            nickname: email,
            phone,
            shop_id: this.appContext.get().shopId,
          },
          {
            headers: this.makeCaptchaHeaders(recaptchaToken),
          }
        );
      }),
      tap((response) => {
        this.tokenRepository.setToken(response.token);
        this.tokenRepository.setRefreshToken(response.refresh_token);
      }),
      concatMap(() => this.getUser())
    );
  }

  /**
   * `POST /v1.0/me/profile` is a full replace, not a patch: it rejects a body with no `email`, and
   * writes `null` over every other field the body omits — verified against the harness. So the write
   * carries the whole profile, read back from `/v1.0/me` first, with the caller's edits overlaid.
   *
   * Reading it here rather than merging the cached `User` is the fix for PAUL-3514. That cache is
   * written at login and nowhere else, so merging it re-sent a stale number (re-judged by the
   * backend, or reverting an edit made in the portal) and, because `User` carries no `nickname`,
   * `gender`, `birthday` or `description`, quietly nulled those four on every checkout Continue.
   */
  updateUser(patch: UpdateUserPayload): Observable<User> {
    const token = this.tokenRepository.getToken();
    if (!token) return of(null);
    return this.getProfile(token).pipe(
      concatMap((current) =>
        this.executeCaptcha('updateProfile').pipe(
          concatMap((recaptchaToken: string) => {
            return this.client.post<Auth>(
              `${this.baseUrl}/v1.0/me/profile`,
              { ...current, ...patch },
              {
                headers: { ...this.makeCaptchaHeaders(recaptchaToken), Authorization: `Bearer ${token}` },
              }
            );
          })
        )
      ),
      concatMap(() => this.getUser())
    );
  }

  /** The profile as the backend currently holds it, in exactly the fields the write replaces. */
  private getProfile(token: string): Observable<ChangeProfileModel> {
    return this.client
      .get<UserResponse>(`${this.baseUrl}/v1.0/me`, { headers: { Authorization: `Bearer ${token}` } })
      .pipe(
        map((me) => ({
          email: me.email,
          phone: me.phone ?? null,
          first_name: me.first_name ?? null,
          last_name: me.last_name ?? null,
          nickname: me.nickname ?? null,
          description: me.description ?? null,
          gender: me.gender ?? null,
          birthday: me.birthday ?? null,
        }))
      );
  }

  getUser(): Observable<User> {
    const token = this.tokenRepository.getToken();
    if (!token) return of(null);
    return zip([
      this.client.get<UserResponse>(`${this.baseUrl}/v1.0/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
      this.client.get<CinevillePassModel[]>(`/api/v1.0/cineville/user-passes`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    ]).pipe(
      map(([user, cvPass]) => {
        return {
          firstname: user.first_name,
          lastname: user.last_name,
          email: user.email,
          phone: user.phone,
          cvPass: cvPass,
          newsletter: user.agreement2,
          shopIds: user.shop_ids ?? null,
        };
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          this.tokenRepository.clear();
        }
        return of(null);
      })
    );
  }

  login(email: string, password: string): Observable<User> {
    return this.executeCaptcha('login').pipe(
      concatMap((recaptchaToken: string) => {
        return this.client.post<AuthResponse>(
          `${this.baseUrl}/v1.0/client/auth`,
          { email, password, shop_id: this.appContext.get().shopId },
          {
            headers: this.makeCaptchaHeaders(recaptchaToken),
            context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true),
          }
        );
      }),
      tap((response) => {
        this.tokenRepository.setToken(response.token);
        this.tokenRepository.setRefreshToken(response.refresh_token);
      }),
      concatMap(() => this.getUser())
    );
  }

  remindPassword(email: string): Observable<boolean> {
    return this.executeCaptcha('login').pipe(
      concatMap((recaptchaToken: string) => {
        return this.client.post(
          `${this.baseUrl}/v1.0/auth/password-forgot`,
          { email, shop_id: this.appContext.get().shopId },
          {
            headers: this.makeCaptchaHeaders(recaptchaToken),
          }
        );
      }),
      map(() => true)
    );
  }

  resetPassword(forgotToken: string, password: string, confirmPassword: string): Observable<boolean> {
    return this.executeCaptcha('login').pipe(
      concatMap((recaptchaToken: string) => {
        return this.client.post(
          `${this.baseUrl}/v1.0/me/password-change/token`,
          {
            forgot_token: forgotToken,
            password_new: password,
            password_confirm: confirmPassword,
          },
          {
            headers: this.makeCaptchaHeaders(recaptchaToken),
            // The reset page tells a dead link apart from a real failure itself; the interceptor would
            // swallow a 401 into EMPTY and leave that page spinning forever.
            context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true),
          }
        );
      }),
      map(() => true)
    );
  }

  exists(email: string): Observable<boolean> {
    return this.executeCaptcha('login').pipe(
      concatMap((recaptchaToken: string) => {
        return this.client
          .post<{ exists: boolean }>(
            `${this.baseUrl}/v1.0/auth/exists`,
            {
              email,
              shop_id: this.appContext.get().shopId,
            },
            {
              headers: this.makeCaptchaHeaders(recaptchaToken),
            }
          )
          .pipe(
            map((response) => {
              return response?.exists;
            }),
            catchError((error) => {
              return of(false);
            })
          );
      })
    );
  }

  /**
   * `ReCaptchaV3Service` is resolved per call, not injected.
   *
   * Its constructor appends Google's `api.js` to `<head>`, and this strategy is built at bootstrap
   * (`AppComponent` → `AuthState` → `AUTH_STRATEGY`), so injecting it directly loaded ~700 kB of
   * reCAPTCHA on every shop page for every buyer — including the guests who check out and never log
   * in. `Injector.get` is the mechanism because the service has to come from the same root injector
   * that `RECAPTCHA_V3_SITE_KEY` is provided in (`AuthModule.forRoot`).
   */
  private executeCaptcha(action: string): Observable<string> {
    return this.injector.get(ReCaptchaV3Service).execute(action);
  }

  private makeCaptchaHeaders(recaptchaToken: string) {
    return {
      'X-Captcha-Response': recaptchaToken,
      'X-Captcha-Site-Key': this.config.recaptchaSiteKey,
    };
  }
}
